/**
 * The day pane's rows and the map's pins, derived from a day's events and the
 * travel segments that have arrived for them. Structural types keep the
 * module free of api-types coupling so it tests in node.
 */

export interface TimelineEvent {
	id: number
	tripDayIndex: number
	eventIndex: number
	location: {
		googlePlaceId: string | null
		placeDetails?: { geometry?: { location: { lat: number; lng: number } } } | null
	} | null
}

export interface TimelineSegment {
	fromEventId: number
	toEventId: number
	tripDayIndex: number
}

/**
 * How the connector under an event draws, mirroring iOS
 * `travelSegmentDisplay`: the route where a segment exists, a spinner while
 * the server is still working one out, and nothing at all between two events
 * at the same place (or with a place missing).
 */
export type GapState = 'hidden' | 'calculating' | 'route'

export type TimelineRow<E extends TimelineEvent = TimelineEvent, S extends TimelineSegment = TimelineSegment> =
	| { kind: 'event'; event: E; position: number }
	| { kind: 'gap'; from: E; to: E; state: GapState; segment: S | null }

const placeOf = (event: TimelineEvent): string | null => event.location?.googlePlaceId ?? null

export const dayTimeline = <E extends TimelineEvent, S extends TimelineSegment>(
	events: E[],
	segments: S[],
): TimelineRow<E, S>[] => {
	const rows: TimelineRow<E, S>[] = []
	events.forEach((event, index) => {
		rows.push({ kind: 'event', event, position: index + 1 })
		const next = events[index + 1]
		if (!next) return
		const segment =
			segments.find(s => s.fromEventId === event.id && s.toEventId === next.id && s.tripDayIndex === event.tripDayIndex) ??
			null
		const origin = placeOf(event)
		const destination = placeOf(next)
		// Same place (or no place at either end): nothing to travel between. A
		// segment that exists is shown; without one, a route is only on its way
		// when both ends have a place to route between.
		const state: GapState =
			origin === destination
				? 'hidden'
				: segment
					? 'route'
					: origin !== null && destination !== null
						? 'calculating'
						: 'hidden'
		rows.push({ kind: 'gap', from: event, to: next, state, segment })
	})
	return rows
}

export interface DayPin {
	eventId: number
	/** The event's 1-based position in the day, so pin "3" is the third card. */
	order: number
	lat: number
	lng: number
}

const coordinates = (event: TimelineEvent): { lat: number; lng: number } | null => {
	const point = event.location?.placeDetails?.geometry?.location
	return point && typeof point.lat === 'number' && typeof point.lng === 'number' ? point : null
}

/** The day's mappable events in order, numbered by their position among all the day's events. */
export const dayPins = (events: TimelineEvent[]): DayPin[] =>
	events.flatMap((event, index) => {
		const point = coordinates(event)
		return point ? [{ eventId: event.id, order: index + 1, lat: point.lat, lng: point.lng }] : []
	})

export interface TripPin extends DayPin {
	dayIndex: number
}

/** Every mappable event on the trip, in day then event order, numbered by its position in its day like {@link dayPins}. */
export const wholeTripPins = (events: TimelineEvent[]): TripPin[] => {
	const sorted = [...events].sort((a, b) => a.tripDayIndex - b.tripDayIndex || a.eventIndex - b.eventIndex)
	const positionInDay = new Map<number, number>()
	return sorted.flatMap(event => {
		const order = (positionInDay.get(event.tripDayIndex) ?? 0) + 1
		positionInDay.set(event.tripDayIndex, order)
		const point = coordinates(event)
		return point ? [{ eventId: event.id, dayIndex: event.tripDayIndex, order, lat: point.lat, lng: point.lng }] : []
	})
}

export interface DayRouteLine {
	fromEventId: number
	toEventId: number
	/** The cached route's encoded polyline; null means a straight line between the two pins. */
	encodedPath: string | null
}

/**
 * The lines joining a day's pins in order: between two neighbouring events the
 * segment's cached route when it has one; a straight line where it has none,
 * or where an unmappable event sits between the two pins.
 */
export const dayRoutes = <E extends TimelineEvent, S extends TimelineSegment>(
	events: E[],
	segments: S[],
	polylineOf: (segment: S) => string | null,
): DayRouteLine[] => {
	const pins = dayPins(events)
	return pins.slice(1).map((to, index) => {
		const from = pins[index]
		const neighbours = to.order === from.order + 1
		const segment = neighbours
			? segments.find(s => s.fromEventId === from.eventId && s.toEventId === to.eventId)
			: undefined
		return { fromEventId: from.eventId, toEventId: to.eventId, encodedPath: segment ? polylineOf(segment) : null }
	})
}

/** The address line under an event's name: the location's secondary text, else its full text; null when there is nothing to show. */
export const eventAddress = (
	location: { secondaryText: string | null; fullText: string } | null | undefined,
): string | null => {
	if (!location) return null
	return location.secondaryText?.trim() || location.fullText.trim() || null
}
